import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ShieldAlert, Brain, Flame, EyeOff, CheckCircle2, XCircle, BookOpen, Clock, Sparkles, ChevronDown, Lock, Star } from 'lucide-react';
import { Button } from '../components/Button';
import { SalesNotification } from '../components/SalesNotification';
declare const fbq: any;
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-brown/10 py-5" data-id="element-19">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full justify-between items-center text-left focus:outline-none group" data-id="element-20">
        <span className="font-serif text-xl md:text-2xl text-brown pr-4 group-hover:text-terracotta transition-colors" data-id="element-21">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-22">
          <ChevronDown className="w-6 h-6 text-terracotta flex-shrink-0" data-id="element-23" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-24">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-25">
            <p className="pt-4 text-brown/80 text-lg leading-relaxed" data-id="element-26">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function LandingPage() {
  return <div className="min-h-screen bg-cream overflow-hidden" data-id="element-27">
      {/* 1. HERO SECTION */}
      <section className="relative pt-12 pb-12 md:pt-12 md:pb-12 px-6 overflow-hidden" data-id="element-28">
        <div className="absolute inset-0 z-0 overflow-hidden" data-id="element-29">
          <img src="https://cdn.magicpatterns.com/uploads/x37Z8jiipj6F82zPaHYD3U/ChatGPT_Image_29_ene_2026,_11_06_56.png" alt="" className="absolute inset-0 w-full h-full object-cover object-top opacity-50" data-id="element-30" />
          <div className="absolute inset-0 bg-cream/50" data-id="element-31" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center text-center gap-10" data-id="element-32">
          <motion.div className="space-y-8 flex flex-col items-center" initial="hidden" animate="visible" variants={staggerContainer} data-id="element-33">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-sage font-bold text-sm border border-sage/40 shadow-md" data-id="element-34">
              <Brain className="w-4 h-4" data-id="element-35" />
              <span data-id="element-36">Basado en Neurociencia y Regulación Emocional</span>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-[3.5rem] text-brown leading-[1.15]" data-id="element-37">
              Descubre cómo calmar las rabietas de tu hijo en{' '}
              <span className="text-terracotta italic underline decoration-terracotta/30 underline-offset-4" data-id="element-38">
                menos de 5 minutos
              </span>{' '}
              — sin gritos, castigos ni culpa.
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-brown/70 leading-relaxed max-w-2xl" data-id="element-39">
              Con el{' '}
              <strong className="text-terracotta font-bold" data-id="element-40">
                Método de Regulación Co-activa
              </strong>{' '}
              (basado en neurociencia), recuperarás la calma en casa y la
              conexión con tu peque 💛
            </motion.p>

            <motion.div variants={fadeInUp} className="pt-2 flex flex-col items-center gap-5 w-full" data-id="element-41">
              <Button fullWidth className="max-w-md py-5 text-xl" onClick={() => document.getElementById('pricing')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-42">
                Obtener Acceso Inmediato
              </Button>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-brown/50" data-id="element-43">
                <span className="flex items-center gap-1.5" data-id="element-44">
                  <Clock className="w-4 h-4" data-id="element-45" /> Acceso inmediato
                </span>
                <span className="flex items-center gap-1.5" data-id="element-46">
                  <Lock className="w-4 h-4" data-id="element-47" /> Pago seguro
                </span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div className="w-full max-w-sm relative mt-4" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4,
          ease: 'easeOut'
        }} data-id="element-48">
            <div className="absolute inset-0 bg-terracotta/8 rounded-3xl blur-2xl scale-110 -z-10" data-id="element-49" />
            <img src="https://cdn.magicpatterns.com/uploads/j8KJtRuKUfgjqwyKWN9igV/Imagen.Hero.png" alt="El Fin de las Rabietas - Guía para padres en tablet" className="w-full h-auto rounded-2xl shadow-soft object-cover" data-id="element-50" />
          </motion.div>
        </div>
      </section>

      {/* 2. OPENING HOOK */}
      <section className="py-12 px-6 bg-white" data-id="element-51">
        <motion.div className="max-w-3xl mx-auto text-center space-y-8" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} data-id="element-52">
          <motion.div variants={fadeInUp} data-id="element-53">
            <Heart className="w-12 h-12 text-terracotta mx-auto mb-6 opacity-80" data-id="element-54" />
            <p className="text-xl md:text-2xl leading-relaxed text-brown font-medium" data-id="element-55">
              "Son las 6 de la tarde. Estás agotada. Y de repente, por un vaso
              de color equivocado o un juguete que no se queda en pie, tu hijo
              estalla. Los gritos llenan la casa, tu paciencia se evapora y
              sientes que, por más que explicas o castigas, nada cambia."
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="w-16 h-px bg-terracotta/30 mx-auto" data-id="element-56" />

          <motion.p variants={fadeInUp} className="text-lg text-brown/80 leading-relaxed" data-id="element-57">
            No estás sola. Pero lo más importante que leerás hoy es esto: las
            rabietas no son una señal de que eres una "mala madre", son una
            desesperada señal de auxilio del cerebro en desarrollo de tu hijo
            que aún no sabe cómo decirte lo que siente.
          </motion.p>
        </motion.div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="py-12 px-6 bg-cream-dark" data-id="element-58">
        <div className="max-w-4xl mx-auto" data-id="element-59">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white p-8 md:p-12 rounded-3xl shadow-soft" data-id="element-60">
            <h2 className="text-3xl md:text-4xl text-center mb-8" data-id="element-61">
              El ciclo de "reacción-culpa"
            </h2>
            <div className="space-y-6 text-lg text-brown/80 leading-relaxed" data-id="element-62">
              <p data-id="element-63">
                La mayoría de las madres viven en un ciclo de "reacción-culpa".
                Intentas razonar con lógica cuando él está en plena crisis (lo
                cual es neurológicamente imposible), terminas gritando porque no
                puedes más, y luego pasas la noche mirando a tu hijo dormir
                sintiéndote la peor persona del mundo.
              </p>
              <p data-id="element-64">
                El problema no es tu falta de autoridad, sino que nos enseñaron
                a criar desde la supresión de emociones ("no llores", "vete a tu
                cuarto", "ahora te vas a enterar"). Esos métodos no enseñan a tu
                hijo a calmarse; solo le enseñan a esconder su dolor por miedo,
                lo que garantiza que la rabieta sea más explosiva la próxima
                vez.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-12 px-6 bg-white" data-id="element-65">
        <div className="max-w-6xl mx-auto" data-id="element-66">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-67">
            <h2 className="text-3xl md:text-5xl mb-6" data-id="element-68">
              Por qué lo que has intentado no funciona
            </h2>
            <p className="text-xl text-brown/70 max-w-2xl mx-auto" data-id="element-69">
              No es tu culpa. Las estrategias tradicionales van en contra de
              cómo funciona el cerebro infantil.
            </p>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-70">
            {[{
            icon: Brain,
            title: 'Intentar razonar',
            desc: "Falla porque el cerebro lógico de tu hijo se 'desconecta' literalmente durante la crisis; es como hablarle a alguien que se está ahogando."
          }, {
            icon: Flame,
            title: 'Gritar',
            desc: 'Solo añade combustible al fuego, elevando los niveles de cortisol y bloqueando cualquier posibilidad de aprendizaje.'
          }, {
            icon: ShieldAlert,
            title: 'Los castigos',
            desc: 'Generan resentimiento y miedo, pero no construyen las rutas neuronales que tu hijo necesita para el autocontrol futuro.'
          }, {
            icon: EyeOff,
            title: 'Ignorarlo',
            desc: "Solo le confirma que su 'puerto seguro' (tú) desaparece cuando él más te necesita, aumentando su ansiedad."
          }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="bg-cream p-8 rounded-2xl flex gap-6 items-start" data-id="element-71">
                <div className="bg-white p-4 rounded-full shadow-sm shrink-0" data-id="element-72">
                  <item.icon className="w-8 h-8 text-terracotta" data-id="element-73" />
                </div>
                <div data-id="element-74">
                  <h3 className="text-2xl mb-3" data-id="element-75">{item.title}</h3>
                  <p className="text-brown/70 leading-relaxed" data-id="element-76">{item.desc}</p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM (Dark Section) */}
      <section className="py-12 px-6 bg-brown text-cream relative overflow-hidden" data-id="element-77">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-terracotta/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3" data-id="element-78" />

        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-79">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-80">
            <Sparkles className="w-12 h-12 text-terracotta mx-auto mb-8" data-id="element-81" />
            <h2 className="text-4xl md:text-5xl mb-8 text-white" data-id="element-82">
              Presentamos el Método de los 5 Minutos <br data-id="element-83" />
              <span className="text-terracotta italic text-3xl md:text-4xl" data-id="element-84">
                (Regulación Co-activa)
              </span>
            </h2>
            <div className="space-y-6 text-lg md:text-xl text-cream/80 leading-relaxed font-light" data-id="element-85">
              <p data-id="element-86">No es magia, es neurociencia aplicada a la crianza.</p>
              <p data-id="element-87">
                En lugar de luchar contra la rabieta, este sistema te enseña a
                usar tu propio sistema nervioso como un "ancla" para el de tu
                hijo. A través de una secuencia exacta de 60 segundos por etapa{' '}
                <strong className="text-white font-medium" data-id="element-88">
                  (Pausar, Conectar, Validar, Redirigir y Cerrar)
                </strong>
                , lograrás que el cerebro de tu hijo pase del modo
                "supervivencia" al modo "calma" en tiempo récord.
              </p>
              <p className="text-2xl text-white font-serif pt-4" data-id="element-89">
                No fuerzas la obediencia; facilitas la regulación.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="py-12 px-6 bg-cream" data-id="element-90">
        <div className="max-w-5xl mx-auto" data-id="element-91">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-92">
            <h2 className="text-4xl md:text-5xl mb-6" data-id="element-93">
              ¿Qué incluye el método?
            </h2>
            <p className="text-xl text-brown/70" data-id="element-94">
              Todo lo que necesitas para transformar el ambiente de tu hogar hoy
              mismo.
            </p>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 gap-6" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-95">
            {[{
            title: "La Guía Maestra 'Fin de las Rabietas'",
            desc: 'Un paso a paso completo desde la neurociencia hasta la acción práctica.'
          }, {
            title: 'El Mapa de los 5 Minutos',
            desc: 'La secuencia exacta para desescalar cualquier crisis sin importar el lugar.'
          }, {
            title: 'Diccionario de Frases de Validación',
            desc: "Qué decir exactamente para 'nombrar y domesticar' la emoción sin usar el destructivo 'pero...'."
          }, {
            title: 'Protocolo de Emergencia en Público',
            desc: 'Cómo manejar la presión social y el juicio de los extraños sin ceder ante el berrinche.'
          }, {
            title: 'Guía de Reparación',
            desc: 'El plan de 4 pasos para cuando tú pierdes el control y necesitas sanar el vínculo con tu hijo.'
          }, {
            title: 'Estrategia de Prevención',
            desc: "Rutinas sencillas y el método 'una vez y se cumple' para reducir la frecuencia de las rabietas antes de que ocurran."
          }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-6 rounded-2xl shadow-sm border border-brown/5 flex gap-4" data-id="element-96">
                <CheckCircle2 className="w-6 h-6 text-sage shrink-0 mt-1" data-id="element-97" />
                <div data-id="element-98">
                  <h4 className="font-serif text-xl mb-2" data-id="element-99">{item.title}</h4>
                  <p className="text-brown/70" data-id="element-100">{item.desc}</p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 7. WHO THIS IS FOR / NOT FOR */}
      <section className="py-12 px-6 bg-white" data-id="element-101">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-102">
          {/* For */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-sage/10 p-8 md:p-10 rounded-3xl" data-id="element-103">
            <h3 className="text-3xl mb-8 flex items-center gap-3" data-id="element-104">
              <Heart className="w-8 h-8 text-sage" data-id="element-105" />
              Esto ES para ti si...
            </h3>
            <ul className="space-y-6" data-id="element-106">
              {['Madres con hijos entre 2 y 7 años que se sienten sobrepasadas por la intensidad emocional de sus pequeños.', 'Cuidadores que quieren romper ciclos generacionales de gritos y castigos físicos.', 'Mamás que trabajan y llegan a casa con poca energía, necesitando soluciones efectivas y rápidas.', 'Padres que buscan una crianza basada en el respeto pero que no quieren caer en la permisividad.'].map((text, i) => <li key={i} className="flex gap-4 items-start" data-id="element-107">
                  <CheckCircle2 className="w-6 h-6 text-sage shrink-0 mt-0.5" data-id="element-108" />
                  <span className="text-brown/80 leading-relaxed" data-id="element-109">{text}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Not For */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-terracotta/5 p-8 md:p-10 rounded-3xl border border-terracotta/10" data-id="element-110">
            <h3 className="text-3xl mb-8 flex items-center gap-3" data-id="element-111">
              <XCircle className="w-8 h-8 text-terracotta" data-id="element-112" />
              Esto NO es para ti si...
            </h3>
            <ul className="space-y-6" data-id="element-113">
              {["Personas que buscan 'trucos' para manipular o silenciar a los niños rápidamente por miedo.", 'Quienes creen que el castigo físico es la única forma de enseñar respeto.', 'Madres que no están dispuestas a trabajar primero en su propia calma antes de pedirla a sus hijos.'].map((text, i) => <li key={i} className="flex gap-4 items-start" data-id="element-114">
                  <XCircle className="w-6 h-6 text-terracotta/60 shrink-0 mt-0.5" data-id="element-115" />
                  <span className="text-brown/80 leading-relaxed" data-id="element-116">{text}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* 8. HOW IT WORKS & PROOF */}
      <section className="py-12 px-6 bg-cream-dark" data-id="element-117">
        <div className="max-w-4xl mx-auto space-y-20" data-id="element-118">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-119">
            <h2 className="text-4xl text-center mb-12" data-id="element-120">¿Cómo funciona?</h2>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-brown/10" data-id="element-121">
              {[{
              step: '1',
              title: 'Descarga la Guía',
              desc: 'Recibe acceso inmediato para leerla desde tu celular o tablet hoy mismo.'
            }, {
              step: '2',
              title: 'Aplica el Primer Minuto',
              desc: 'La próxima vez que empiece el caos, aplica la técnica de respiración y pausa que aprenderás en el capítulo 11.'
            }, {
              step: '3',
              title: 'Observa el Cambio',
              desc: 'Mira cómo, al cambiar tu respuesta, la frecuencia y duración de las rabietas disminuye drásticamente en menos de 7 días.'
            }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-122">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-cream-dark bg-terracotta text-white font-serif text-xl shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-md z-10" data-id="element-123">
                    {item.step}
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-6 rounded-2xl shadow-sm" data-id="element-124">
                    <h4 className="font-serif text-2xl mb-2" data-id="element-125">{item.title}</h4>
                    <p className="text-brown/70" data-id="element-126">{item.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white p-8 md:p-12 rounded-3xl shadow-soft text-center" data-id="element-127">
            <Star className="w-10 h-10 text-terracotta mx-auto mb-6 fill-terracotta/20" data-id="element-128" />
            <p className="text-xl md:text-2xl text-brown leading-relaxed italic font-serif" data-id="element-129">
              "En solo una semana aplicando la regulación co-activa, notarás que
              las explosiones de 20 minutos se convierten en momentos de
              conexión de 5 minutos. No solo las rabietas se detienen más
              rápido; notarás que tu hijo empieza a buscarte para contarte lo
              que siente antes de estallar, porque ahora sabe que tú eres un
              lugar seguro, no un juez."
            </p>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-12 px-6 bg-white" data-id="element-130">
        <div className="max-w-6xl mx-auto" data-id="element-131">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-132">
            <h2 className="text-4xl md:text-5xl mb-6" data-id="element-133">
              Lo que dicen otras familias
            </h2>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-134">
            {[{
            name: 'Elena R.',
            location: 'Madrid',
            image: 'https://cdn.magicpatterns.com/uploads/4bcr3NrQhyaZ2n6DmRu61P/Elena.R.png',
            quote: 'Soy madre de dos y trabajo a tiempo completo. Sinceramente, no tengo tiempo para leer teorías eternas. Compré la guía a las 11 de la noche después de un día horrible de gritos. Lo que me salvó fue el Mapa de los 5 Minutos. Lo apliqué a la mañana siguiente en medio de una crisis por los zapatos y, por primera vez en meses, no terminamos los dos llorando. Es práctico, directo y va al grano. Ojalá lo hubiera tenido hace dos años.',
            stars: '⭐⭐⭐⭐⭐'
          }, {
            name: 'Marcos T.',
            location: 'Ciudad de México',
            image: 'https://cdn.magicpatterns.com/uploads/qTLas9qoHyHGKvGCXc6uZn/Marcos.T.png',
            quote: "Confieso que era de los que pensaba que a las rabietas se les ponía fin con 'mano dura' y castigos, pero solo estaba logrando que mi hijo me tuviera miedo. Esta guía me abrió los ojos sobre cómo funciona su cerebro. Aplicar la Validación Emocional no es ser permisivo, es ser inteligente. Mi relación con mi hijo de 4 años ha cambiado por completo; ahora él confía en mí para calmarse en lugar de esconderse.",
            stars: '⭐⭐⭐⭐⭐'
          }, {
            name: 'Lucía M.',
            location: 'Buenos Aires',
            image: 'https://cdn.magicpatterns.com/uploads/8HuRjsAbbDg9XsgRt4TCAZ/Lucia.M.png',
            quote: "Lo que más me dolió siempre fue esa sensación de culpa al ver a mi hija dormir después de haberle gritado todo el día. 'El Fin de las Rabietas' no solo me dio herramientas para ella, sino que me enseñó a calmarme a mí misma primero. El Protocolo de Reparación es oro puro. Ya no me siento una mala madre, me siento una mamá preparada. Mi casa al fin se siente como un hogar tranquilo.",
            stars: '⭐⭐⭐⭐⭐'
          }, {
            name: 'Carla J.',
            location: 'Bogotá',
            image: 'https://cdn.magicpatterns.com/uploads/f87w35NYSDXVyCbGJkXXK5/Carla.J.png',
            quote: "Pensé que nada funcionaría con mi hijo porque tiene una personalidad muy fuerte y las rabietas eran extremas, incluso en público. Usé la Estrategia de Emergencia en el supermercado la semana pasada y la diferencia fue increíble. No hubo drama, no hubo miradas de juicio de extraños, solo una conexión real entre nosotros. Si crees que tu hijo es 'imposible', prueba este método.",
            stars: '⭐⭐⭐⭐⭐'
          }].map((t, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl shadow-sm border border-brown/5 flex flex-col gap-6" data-id="element-135">
                <div className="flex items-center gap-4" data-id="element-136">
                  <img src={t.image} alt={t.name} className="w-16 h-16 rounded-full object-cover" data-id="element-137" />
                  <div data-id="element-138">
                    <h4 className="font-serif text-xl text-brown" data-id="element-139">{t.name}</h4>
                    <p className="text-sm text-brown/60" data-id="element-140">{t.location}</p>
                    <div className="text-sm mt-1" data-id="element-141">{t.stars}</div>
                  </div>
                </div>
                <p className="text-brown/80 leading-relaxed italic" data-id="element-142">
                  "{t.quote}"
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-12 px-6 bg-cream" data-id="element-143">
        <div className="max-w-3xl mx-auto" data-id="element-144">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-145">
            <h2 className="text-4xl md:text-5xl mb-6" data-id="element-146">Preguntas Frecuentes</h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="bg-white p-6 md:p-10 rounded-3xl shadow-sm" data-id="element-147">
            <FAQItem question="¿Funciona con niños de 2 años?" answer="Sí, está diseñado específicamente para la etapa de desarrollo donde el cerebro emocional es dominante." data-id="element-148" />
            <FAQItem question="¿Qué pasa si mi hijo es muy terco?" answer="No existe la terquedad, existe la necesidad de autonomía. El método enseña cómo darles control sin ceder en tus límites." data-id="element-149" />
            <FAQItem question="¿Y si yo soy la que siempre pierde los nervios?" answer="La guía incluye una sección completa de 'Autocontrol para Mamás' para que tú también sanes tu sistema nervioso." data-id="element-150" />
            <FAQItem question="¿Es mucho texto para leer?" answer="No. Está diseñado para madres con tiempo limitado, con tablas, listas y puntos clave para ir directo a la solución." data-id="element-151" />
          </motion.div>
        </div>
      </section>

      {/* 10. OFFER & CTA */}
      <section id="pricing" className="py-12 px-6 bg-white relative overflow-hidden" data-id="element-152">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cream-dark via-white to-white -z-10" data-id="element-153" />

        <div className="max-w-4xl mx-auto text-center" data-id="element-154">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="space-y-8" data-id="element-155">
            <h2 className="text-4xl md:text-5xl leading-tight" data-id="element-156">
              Transforma la atmósfera de tu hogar hoy mismo.
            </h2>

            <div className="bg-cream-dark p-8 md:p-12 rounded-3xl border border-brown/10 shadow-xl shadow-brown/5 max-w-2xl mx-auto relative" data-id="element-157">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-sage text-white px-6 py-2 rounded-full text-sm font-bold tracking-wider uppercase shadow-md" data-id="element-158">
                Oferta Especial
              </div>

              <div className="flex justify-center items-baseline gap-2 mb-6 mt-4" data-id="element-159">
                <span className="text-6xl md:text-7xl font-serif text-brown" data-id="element-160">
                  $6.39
                </span>
                <span className="text-xl text-brown/60 font-medium" data-id="element-161">USD</span>
              </div>

              <p className="text-lg text-brown/80 mb-8 font-medium" data-id="element-162">
                Pago único. Acceso de por vida.
              </p>

              <p className="text-brown/70 mb-10 leading-relaxed" data-id="element-163">
                Obtendrás todas las herramientas para que las tardes vuelvan a
                ser de paz y no de batallas. Es menos de lo que cuesta un café
                especial, pero con un impacto que durará toda la vida de tu
                hijo.
              </p>

              <Button fullWidth className="py-5 text-xl shadow-glow" onClick={() => {
              if (typeof fbq === 'function') {
                fbq('track', 'AddToCart');
              }
              window.location.href = 'https://dobolia.com/cart/64131354362205:1';
            }} data-id="element-164">
                QUIERO EL MÉTODO FIN DE LAS RABIETAS AHORA
              </Button>

              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-brown/60" data-id="element-165">
                <Lock className="w-4 h-4" data-id="element-166" />
                <span data-id="element-167">Pago 100% seguro. Acceso inmediato tras la compra.</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 text-center text-brown/40 text-sm bg-white border-t border-brown/5" data-id="element-168">
        <p data-id="element-169">
          © {new Date().getFullYear()} El Fin de las Rabietas. Todos los
          derechos reservados.
        </p>
      </footer>

      <SalesNotification data-id="element-170" />
    </div>;
}